<template>
  <div class="hris-page privacy-policy">
    <div class="hris-page-inner hris-form-narrow">
      <div>
        <q-btn flat unelevated class="hris-q-ghost go-back-link" label="Go back" @click="goBack"></q-btn>
      </div>

      <div class="hris-page-header">
        <span class="hris-page-eyebrow">Legal</span>
        <h1 class="hris-page-title">BeaPayday Data Privacy & Protection Policy</h1>
      </div>

      <div class="section">
        <h2>1. Introduction</h2>
        <p>BeaPayday is committed to protecting the privacy, confidentiality, and security of all personal data processed
          within our Complete Human Resource Information System (CHRIS). This policy explains how employee and applicant
          information is collected, used, stored, and protected in compliance with applicable data privacy laws and best
          practices.</p>
        <p>Our HRIS is designed with privacy, security, and compliance at its core to ensure responsible handling of
          sensitive HR information.</p>
      </div>

      <div class="section">
        <h2>2. Scope</h2>
        <p>This policy applies to all personal and employment-related data processed within the HRIS, including data
          related to:</p>
        <ul>
          <li>Employees</li>
          <li>Job applicants</li>
          <li>Contractors and consultants</li>
          <li>Interns and trainees</li>
          <li>Former employees</li>
        </ul>
        <p>It covers all users of the system, including HR staff, managers, system administrators, and authorized third
          parties.</p>
      </div>

      <div class="section">
        <h2>3. Personal Data We Process</h2>
        <h3>A. Basic Personal Information</h3>
        <ul>
          <li>Full name</li>
          <li>Address</li>
          <li>Contact details (phone number, email)</li>
          <li>Date of birth</li>
          <li>Gender</li>
          <li>Civil status</li>
          <li>Nationality</li>
        </ul>

        <h3>B. Employment Information</h3>
        <ul>
          <li>Employee ID</li>
          <li>Job title and department</li>
          <li>Employment history</li>
          <li>Attendance and leave records</li>
          <li>Performance evaluations</li>
          <li>Training records</li>
          <li>Disciplinary records</li>
        </ul>

        <h3>C. Government & Payroll Information</h3>
        <ul>
          <li>Tax identification numbers</li>
          <li>Social security and government benefit numbers</li>
          <li>Bank account details (for payroll)</li>
          <li>Compensation and benefits information</li>
        </ul>

        <h3>D. Sensitive Personal Information (where applicable)</h3>
        <ul>
          <li>Health or medical information (for benefits or leave)</li>
          <li>Background check results</li>
          <li>Emergency contact details</li>
        </ul>
        <p>Sensitive information is processed with additional safeguards and restricted access.</p>
      </div>

      <div class="section">
        <h2>4. Purpose of Data Processing</h2>
        <p>Personal data within the HRIS is processed only for legitimate and lawful purposes, including:</p>
        <ul>
          <li>Recruitment and hiring</li>
          <li>Employee onboarding and records management</li>
          <li>Payroll and compensation processing</li>
          <li>Benefits administration</li>
          <li>Timekeeping and attendance tracking</li>
          <li>Performance management</li>
          <li>Training and development</li>
          <li>Compliance with labor, tax, and social security laws</li>
          <li>Internal reporting and workforce planning</li>
        </ul>
        <p>We do not process personal data for purposes incompatible with those listed above without proper notice and
          legal basis.</p>
      </div>

      <div class="section">
        <h2>5. Legal Basis for Processing</h2>
        <ul>
          <li>Fulfillment of employment contracts</li>
          <li>Compliance with legal and regulatory obligations</li>
          <li>Legitimate business interests (e.g., workforce management)</li>
          <li>Employee consent, where required</li>
        </ul>
      </div>

      <div class="section">
        <h2>6. Data Protection Principles</h2>
        <ul>
          <li>Transparency – Individuals are informed how their data is used.</li>
          <li>Legitimate Purpose – Data is processed only for lawful HR-related purposes.</li>
          <li>Proportionality – Only necessary data is collected and processed.</li>
          <li>Accuracy – Records are kept accurate and up to date.</li>
          <li>Storage Limitation – Data is retained only as long as required.</li>
          <li>Integrity & Confidentiality – Data is protected against unauthorized access or loss.</li>
        </ul>
      </div>

      <div class="section">
        <h2>7. Access Control</h2>
        <ul>
          <li>Role-based access (HR, managers, payroll, etc.)</li>
          <li>Unique user accounts and strong password policies</li>
          <li>Multi-factor authentication (where enabled)</li>
          <li>Regular review of access rights</li>
          <li>Immediate revocation of access upon employee separation</li>
        </ul>
        <p>Only authorized personnel can access sensitive personal information.</p>
      </div>

      <div class="section">
        <h2>8. Data Security Measures</h2>
        <ul>
          <li>Data encryption (in transit and at rest, where applicable)</li>
          <li>Secure servers and firewalls</li>
          <li>Regular system updates and patch management</li>
          <li>Activity logging and audit trails</li>
          <li>Backup and disaster recovery mechanisms</li>
          <li>Protection against malware and cyber threats</li>
        </ul>
      </div>

      <div class="section">
        <h2>9. Data Sharing and Disclosure</h2>
        <p>Personal data may be shared only when necessary and legally allowed, such as with:</p>
        <ul>
          <li>Payroll and benefits providers</li>
          <li>Government agencies (tax, social security, labor authorities)</li>
          <li>Healthcare or insurance providers</li>
          <li>Authorized auditors or legal advisors</li>
        </ul>
        <p>All third parties are required to follow strict data protection and confidentiality standards.</p>
        <p>We do not sell employees’ personal data.</p>
      </div>

      <div class="section">
        <h2>10. Data Retention</h2>
        <p>Personal data is retained only for as long as necessary for:</p>
        <ul>
          <li>Employment purposes</li>
          <li>Legal and regulatory compliance</li>
          <li>Defense of legal claims</li>
        </ul>
        <p>After the retention period, data is securely deleted or anonymized.</p>
      </div>

      <div class="section">
        <h2>11. Employee Rights</h2>
        <p>Employees and data subjects have the right to:</p>
        <ul>
          <li>Access their personal data</li>
          <li>Request correction of inaccurate information</li>
          <li>Request deletion where legally permissible</li>
          <li>Object to certain types of processing</li>
          <li>Be informed of data breaches affecting their data</li>
        </ul>
        <p>Requests may be submitted to HR or the designated Data Protection Officer (DPO).</p>
      </div>

      <div class="section">
        <h2>12. Data Breach Management</h2>
        <ul>
          <li>Incidents are promptly investigated</li>
          <li>Affected individuals and authorities are notified when required by law</li>
          <li>Corrective actions are taken to prevent recurrence</li>
        </ul>
      </div>

      <div class="section">
        <h2>13. Confidentiality Obligations</h2>
        <p>All personnel with access to the HRIS are bound by confidentiality obligations. Unauthorized access,
          disclosure, or misuse of personal data may result in disciplinary action and legal consequences.</p>
      </div>

      <div class="section">
        <h2>14. Policy Updates</h2>
        <p>This policy may be updated to reflect legal, regulatory, or system changes. Updated versions will be
          communicated to relevant stakeholders.</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  methods: {
    goBack() {
      this.$router.back()
    }
  }
}
</script>

<style scoped>
.privacy-policy .go-back-link {
  margin-bottom: 20px;
}

.privacy-policy .hris-page-header {
  margin-bottom: 32px;
}

.privacy-policy h1 {
  text-align: left;
}

.privacy-policy h2 {
  font-size: 1.375rem;
  font-weight: 700;
  letter-spacing: -0.015em;
  margin-top: 0;
  margin-bottom: 12px;
  color: var(--hris-text);
}

.privacy-policy h3 {
  font-size: 1.0625rem;
  font-weight: 600;
  margin-top: 20px;
  margin-bottom: 8px;
  color: var(--hris-text);
}

.privacy-policy ul {
  list-style-type: disc;
  padding-left: 1.4rem;
  margin-bottom: 1rem;
  color: var(--hris-text-secondary);
  line-height: 1.7;
}

.privacy-policy ul li {
  margin-bottom: 4px;
}

.privacy-policy p {
  font-size: 0.9375rem;
  line-height: 1.7;
  color: var(--hris-text-secondary);
  margin-bottom: 1rem;
}

.privacy-policy .section {
  margin-bottom: 30px;
  padding-bottom: 22px;
  border-bottom: 1px solid var(--hris-border-light);
}

.privacy-policy .section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}
</style>
